/**
 * Circular arc reconstruction.
 *
 * Frame: `x = easting`, `y = northing`. Math angle `t = atan2(dy, dx)` grows
 * counter-clockwise from east. Azimuth `az = atan2(dx, dy)` grows clockwise from
 * north. `rot="ccw"` therefore means increasing `t`.
 *
 * LandXML curves are described redundantly (coordinates *and* radius, delta,
 * chord, tangent, external, midOrd, dirStart, dirEnd) and the redundant values
 * disagree in real files - typically because bearings are rounded to 6 decimals
 * while coordinates carry 12. Coordinates and `rot` are treated as truth; every
 * other attribute is a checksum that produces a warning when it disagrees.
 */

import type { CurveEl, Pt } from '../landxml/types';

export const TAU = Math.PI * 2;

export interface ArcSolution {
  cx: number;
  cy: number;
  r: number;
  /** Math angle of the start point about the centre. */
  t0: number;
  /** Positive swept angle, always in (0, 2*PI]. */
  sweep: number;
  /** +1 for ccw, -1 for cw. */
  sgn: 1 | -1;
  length: number;
  azStart: number;
  azEnd: number;
  /** How the solution was obtained, for the inspector. */
  method: 'center' | 'radius-delta' | 'chord-radius' | 'midord' | 'degenerate';
}

export function azimuth(dx: number, dy: number): number {
  return Math.atan2(dx, dy);
}

export function normAz(a: number): number {
  const r = a % TAU;
  return r < 0 ? r + TAU : r;
}

/** Unit tangent for an azimuth, in (east, north). */
export function tangentOf(az: number): [number, number] {
  return [Math.sin(az), Math.cos(az)];
}

function have(p: Pt | undefined): p is Pt & { x: number; y: number } {
  return !!p && p.x !== undefined && p.y !== undefined;
}

/**
 * Solve the arc. Returns `null` when nothing usable is present, in which case the
 * caller should fall back to a straight chord.
 */
export function solveArc(c: CurveEl, warn?: (m: string) => void): ArcSolution | null {
  const sgn: 1 | -1 = c.rot === 'ccw' ? 1 : -1;

  // (1) Start + Center + End - the only fully determined form.
  if (have(c.start) && have(c.center) && have(c.end)) {
    const r = Math.hypot(c.start.x - c.center.x, c.start.y - c.center.y);
    const t0 = Math.atan2(c.start.y - c.center.y, c.start.x - c.center.x);
    const t1 = Math.atan2(c.end.y - c.center.y, c.end.x - c.center.x);
    let sweep = sgn * (t1 - t0);
    sweep = ((sweep % TAU) + TAU) % TAU;
    if (sweep < 1e-12) sweep = TAU; // start == end: a full circle
    const sol = finish(c.center.x, c.center.y, r, t0, sweep, sgn, 'center');
    check(c, sol, warn);
    return sol;
  }

  // (2) Start + radius + delta + dirStart.
  if (have(c.start) && c.radius && c.delta !== undefined && c.dirStart !== undefined) {
    const r = c.radius;
    const az = c.dirStart;
    // Left normal of travel, in (east, north).
    const nx = -Math.cos(az);
    const ny = Math.sin(az);
    const cx = c.start.x + sgn * r * nx;
    const cy = c.start.y + sgn * r * ny;
    const t0 = Math.atan2(c.start.y - cy, c.start.x - cx);
    const sol = finish(cx, cy, r, t0, Math.abs(c.delta), sgn, 'radius-delta');
    check(c, sol, warn);
    return sol;
  }

  // (3) Start + End + radius: two centres, pick the one matching rot and length.
  if (have(c.start) && have(c.end) && c.radius) {
    const r = Math.abs(c.radius);
    const mx = (c.start.x + c.end.x) / 2;
    const my = (c.start.y + c.end.y) / 2;
    const dx = c.end.x - c.start.x;
    const dy = c.end.y - c.start.y;
    const d = Math.hypot(dx, dy) / 2;
    if (d > 1e-12) {
      const h = Math.sqrt(Math.max(0, r * r - d * d));
      // Unit normal to the chord.
      const ux = -dy / (2 * d);
      const uy = dx / (2 * d);
      const cands: ArcSolution[] = [];
      for (const s of [1, -1]) {
        const cx = mx + s * h * ux;
        const cy = my + s * h * uy;
        const t0 = Math.atan2(c.start.y - cy, c.start.x - cx);
        const t1 = Math.atan2(c.end.y - cy, c.end.x - cx);
        let sweep = sgn * (t1 - t0);
        sweep = ((sweep % TAU) + TAU) % TAU;
        cands.push(finish(cx, cy, r, t0, sweep, sgn, 'chord-radius'));
      }
      let best = cands[0];
      if (c.length !== undefined) {
        best = cands.reduce((a, b) =>
          Math.abs(b.length - c.length!) < Math.abs(a.length - c.length!) ? b : a);
      } else if (c.delta !== undefined) {
        best = cands.reduce((a, b) =>
          Math.abs(b.sweep - Math.abs(c.delta!)) < Math.abs(a.sweep - Math.abs(c.delta!)) ? b : a);
      } else {
        // Without a length or delta, prefer the minor arc - by far the common case.
        best = cands.reduce((a, b) => (b.sweep < a.sweep ? b : a));
      }
      check(c, best, warn);
      return best;
    }
  }

  // (4) Start + End + midOrd (or chord) - derive the radius, then reuse (3).
  if (have(c.start) && have(c.end) && (c.midOrd || c.chord)) {
    const chord = c.chord ?? Math.hypot(c.end.x - c.start.x, c.end.y - c.start.y);
    if (c.midOrd && c.midOrd > 1e-12) {
      const m = c.midOrd;
      const r = (chord * chord) / (8 * m) + m / 2;
      return solveArc({ ...c, radius: r, midOrd: undefined }, warn);
    }
  }

  return null;
}

function finish(
  cx: number, cy: number, r: number, t0: number, sweep: number, sgn: 1 | -1,
  method: ArcSolution['method'],
): ArcSolution {
  const t1 = t0 + sgn * sweep;
  // Azimuth of travel = tangent direction = radial angle rotated by +-90 degrees.
  const azStart = normAz(Math.PI / 2 - (t0 + sgn * Math.PI / 2));
  const azEnd = normAz(Math.PI / 2 - (t1 + sgn * Math.PI / 2));
  return { cx, cy, r, t0, sweep, sgn, length: r * sweep, azStart, azEnd, method };
}

function check(c: CurveEl, sol: ArcSolution, warn?: (m: string) => void): void {
  if (!warn) return;
  const scale = Math.max(1, sol.r);
  if (c.radius !== undefined && Math.abs(Math.abs(c.radius) - sol.r) > 1e-4 * scale) {
    warn(`Curve "${c.name ?? ''}": declared radius ${c.radius} vs ${sol.r.toFixed(6)} from coordinates`);
  }
  if (c.length !== undefined && c.crvType !== 'chord' &&
      Math.abs(c.length - sol.length) > 1e-4 * Math.max(1, c.length)) {
    warn(`Curve "${c.name ?? ''}": declared length ${c.length} vs ${sol.length.toFixed(6)} from coordinates`);
  }
  if (c.delta !== undefined && Math.abs(Math.abs(c.delta) - sol.sweep) > 1e-6) {
    warn(`Curve "${c.name ?? ''}": declared delta ${(c.delta * 180 / Math.PI).toFixed(6)} deg vs ${(sol.sweep * 180 / Math.PI).toFixed(6)} deg`);
  }
}

/** Point at parameter `u` in [0, 1] along the arc. */
export function arcAt(sol: ArcSolution, u: number): [number, number] {
  const t = sol.t0 + sol.sgn * sol.sweep * u;
  return [sol.cx + sol.r * Math.cos(t), sol.cy + sol.r * Math.sin(t)];
}

/**
 * Segment count for a chord-height tolerance `tol` (in model units).
 * `n = ceil(sweep / (2 * acos(1 - tol/r)))`, clamped to something sane.
 */
export function arcSegments(sol: ArcSolution, tol: number): number {
  if (!(sol.r > 0) || !(tol > 0)) return 32;
  const ratio = 1 - tol / sol.r;
  if (ratio <= -1) return 8;
  const step = 2 * Math.acos(Math.min(1, Math.max(-1, ratio)));
  if (!(step > 1e-9)) return 4096;
  return Math.min(4096, Math.max(2, Math.ceil(sol.sweep / step)));
}

/** Derived curve quantities, for the inspector. */
export function arcDerived(sol: ArcSolution) {
  const half = sol.sweep / 2;
  return {
    chord: 2 * sol.r * Math.sin(half),
    tangent: sol.r * Math.tan(half),
    external: sol.r * (1 / Math.cos(half) - 1),
    midOrd: sol.r * (1 - Math.cos(half)),
    deltaDeg: (sol.sweep * 180) / Math.PI,
  };
}
